import React, { useState, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { apiUrl } from '../../ApiHelper/ApiUrl';

import {
  Shield, CheckCircle, AlertCircle, Calendar,
  TrendingUp, TrendingDown, Clock, CheckSquare,
  FileCheck, AlertTriangle, ArrowRight, Activity, PlusCircle, LayoutDashboard
} from 'lucide-react';

import Modal from '../../components/customModal';
import Card from '../../components/card';
import CardHeader from '../../components/cardHeader';
import { useNavigate } from 'react-router-dom'; // Add this at the top
import { RoutesLink } from '../../ApiHelper/RoutesLink';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardReport } from '../../redux/features/reports/reportSlice';

import { Spin, Layout } from 'antd';
const { Content } = Layout;

Chart.register(...registerables);

// --- ROW 1: KPI SUMMARY ---
const StatCard = ({ title, value, trend, icon, colorTheme, trendDown, onClick }) => {
  const themes = {
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
    slate: 'bg-slate-50 text-slate-600 border-slate-100'
  };

  return (
    <Card className="p-6 relative overflow-hidden group" onClick={onClick}>
      <div className="flex justify-between items-start mb-6">
        <div className={`p-3.5 rounded-2xl border ${themes[colorTheme]}`}>
          {React.cloneElement(icon, { size: 24, strokeWidth: 2.5 })}
        </div>
        {trend && (
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${trendDown ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
            }`}>
            {trendDown ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
            {trend}
          </div>
        )}
      </div>
      <div>
        <h3 className="text-3xl font-black text-slate-900 tracking-tight mb-1">{value}</h3>
        <p className="text-sm font-bold text-slate-500  tracking-wider">{title}</p>
      </div>
    </Card>
  );
};

// --- ROW 2: COMPLIANCE STATUS ---
const FrameworkProgress = ({ onClick }) => {
  const frameworks = [
    { name: 'SOC 2', progress: 72, color: 'bg-indigo-500' },
    // { name: 'ISO 27001', progress: 48, color: 'bg-blue-500' },
    { name: 'HIPAA', progress: 64, color: 'bg-emerald-500' },
  ];

  return (
    <Card className="p-7 h-full flex flex-col" onClick={onClick}>
      <CardHeader title="Framework Progress" subtitle="Completion rate by standard" icon={Shield} colorClass="bg-indigo-50 text-indigo-600" />
      <div className="space-y-6 flex-1 justify-center flex flex-col">
        {frameworks.map((fw, i) => (
          <div key={i}>
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-slate-700">{fw.name}</span>
              <span className="font-bold text-slate-900">{fw.progress}%</span>
            </div>
            <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${fw.color} rounded-full transition-all duration-1000`} style={{ width: `${fw.progress}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const ControlsByStatusChart = ({ details, onClick }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) chartInstance.current.destroy();
      chartInstance.current = new Chart(chartRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Passing', 'Needs Review', 'Failing', 'Not Started'],
          datasets: [{
            data: details,
            backgroundColor: ['#10b981', '#f59e0b', '#f43f5e', '#94a3b8'],
            borderWidth: 2,
            borderColor: '#ffffff',
            borderWidth: 4,
            hoverOffset: 8
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '75%',
          plugins: { legend: { display: false } },
          events: [] // Disables chart click so the Card click triggers instead
        }
      });
    }
  }, [details]);

  return (
    <Card className="p-7 h-full" onClick={onClick}>
      <CardHeader title="Control Status" subtitle="Overall framework health" icon={CheckCircle} colorClass="bg-emerald-50 text-emerald-600" />
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div className="relative h-44 w-44 shrink-0">
          <canvas ref={chartRef}></canvas>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-black text-slate-900">{details.reduce((a, b) => a + b, 0)}</span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 w-full">
          {['Passing', 'Review', 'Failing', 'Not Started'].map((label, i) => (
            <div key={label} className="p-3 rounded-2xl bg-slate-50 border border-slate-100/50">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-emerald-500' : i === 1 ? 'bg-orange-500' : i === 2 ? 'bg-rose-500' : 'bg-slate-300'}`} />
                <span className="text-[10px] font-bold text-slate-400 uppercase">{label}</span>
              </div>
              <div className="text-xl font-black text-slate-800">{details[i] || 0}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

// --- ROW 3: OPERATIONS STATUS ---
const WorkflowTasks = ({ onClick }) => (
  <Card className="p-7 h-full" onClick={onClick}>
    <CardHeader title="Workflow Tasks" subtitle="Compliance activity summary" icon={CheckSquare} colorClass="bg-blue-50 text-blue-600" />
    <div className="space-y-4">
      {[
        { task: 'Completed', count: 42, tag: 'Done', tagColor: 'bg-emerald-100 text-emerald-700' },
        { task: 'Open Tasks', count: 14, tag: 'To Do', tagColor: 'bg-slate-100 text-slate-700' },
        { task: 'Due This Week', count: 5, tag: 'Pending', tagColor: 'bg-amber-100 text-amber-700' },
        { task: 'Overdue', count: 3, tag: 'Urgent', tagColor: 'bg-rose-100 text-rose-700' },
      ].map((item, i) => (
        <div key={i} className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-colors group">
          <span className="font-bold text-slate-700 group-hover:text-blue-700">{item.task}</span>
          <div className="flex items-center gap-3">
            <span className="font-black text-slate-900">{item.count}</span>
            <span className={`px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold ${item.tagColor}`}>{item.tag}</span>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

const EvidenceStatus = ({ onClick }) => (
  <Card className="p-7 h-full flex flex-col" onClick={onClick}>
    <CardHeader title="Evidence Status" subtitle="Collection readiness" icon={FileCheck} colorClass="bg-purple-50 text-purple-600" />
    <div className="grid grid-cols-2 gap-4 flex-1">
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-2xl border border-emerald-100 flex flex-col justify-center">
        <span className="text-3xl font-black text-emerald-700">120</span>
        <span className="text-sm font-bold text-emerald-600 mt-1">Uploaded</span>
      </div>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-2xl border border-amber-100 flex flex-col justify-center">
        <span className="text-3xl font-black text-amber-700">25</span>
        <span className="text-sm font-bold text-amber-600 mt-1">Pending</span>
      </div>
      <div className="col-span-2 bg-rose-50 p-4 rounded-2xl border border-rose-100 flex items-center justify-between">
        <span className="font-bold text-rose-700">Rejected Evidence</span>
        <span className="font-black text-rose-900 bg-white px-3 py-1 rounded-lg shadow-sm border border-rose-100">4 Items</span>
      </div>
    </div>
  </Card>
);

// --- ROW 4: ACTIVITY & ALERTS ---
const RecentActivity = ({ onClick }) => (
  <Card className="p-7 h-full" onClick={onClick}>
    <CardHeader title="Recent Activity" icon={Activity} colorClass="bg-slate-100 text-slate-600" />
    <div className="space-y-6">
      {[
        { name: 'John', action: 'uploaded evidence for CC6.1', time: 'Recently', initials: 'JH', color: 'bg-indigo-500' },
        { name: 'Auditor', action: 'reviewed evidence for CC7.2', time: 'Recently', initials: 'AU', color: 'bg-emerald-500' },
        { name: 'System', action: 'marked Control CC5.4 as failing', time: 'Recently', initials: 'SYS', color: 'bg-rose-500' },
      ].map((item, i) => (
        <div key={i} className="flex gap-4">
          <div className={`w-10 h-10 rounded-full text-white font-bold flex items-center justify-center shrink-0 shadow-sm ${item.color}`}>
            {item.initials}
          </div>
          <div>
            <p className="text-sm text-slate-600"><span className="font-bold text-slate-900">{item.name}</span> {item.action}</p>
            <p className="text-xs font-bold text-slate-400 mt-1">{item.time}</p>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

const UpcomingDeadlines = ({ onClick }) => (
  <Card className="p-7 h-full" onClick={onClick}>
    <CardHeader title="Upcoming Deadlines" icon={Clock} colorClass="bg-amber-50 text-amber-600" />
    <div className="space-y-4">
      {[
        { date: 'Mar 18', task: 'Evidence upload due - CC6.1' },
        { date: 'Mar 22', task: 'Access review' },
        { date: 'Mar 30', task: 'Quarterly risk review' },
      ].map((item, i) => (
        <div key={i} className="flex gap-4 items-center group">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-2 text-center min-w-[65px] group-hover:bg-amber-50 group-hover:border-amber-200 transition-colors">
            <div className="text-xs font-bold text-slate-500 uppercase">{item.date.split(' ')[0]}</div>
            <div className="text-lg font-black text-slate-900">{item.date.split(' ')[1]}</div>
          </div>
          <p className="font-bold text-slate-700 text-sm group-hover:text-amber-700 transition-colors">{item.task}</p>
        </div>
      ))}
    </div>
  </Card>
);

const ActionRequired = ({ onClick }) => (
  <Card className="p-7 h-full bg-gradient-to-br from-rose-50 to-red-50 border-rose-100" onClick={onClick}>
    <CardHeader title="Action Required" icon={AlertTriangle} colorClass="bg-rose-100 text-rose-600" />
    <div className="space-y-4">
      {[
        { title: '3 Controls need evidence', desc: 'Pending evidence upload' },
        { title: '2 Issues need remediation', desc: 'Flagged for remediation' },
        { title: '1 Evidence rejected', desc: 'Requires re-submission' }
      ].map((item, i) => (
        <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-rose-100 cursor-pointer hover:border-rose-300 transition-colors">
          <h4 className="font-bold text-rose-700 text-sm">{item.title}</h4>
          <p className="text-xs font-medium text-slate-500 mt-1">{item.desc}</p>
        </div>
      ))}
      <button className="w-full mt-2 py-3 bg-white text-rose-600 font-bold rounded-xl text-sm border border-rose-200 hover:bg-rose-600 hover:text-white transition-colors flex items-center justify-center gap-2 pointer-events-none">
        View All Actions <ArrowRight size={16} />
      </button>
    </div>
  </Card>
);

// --- MAIN DASHBOARD COMPONENT ---
const Dashboard = ({ onNewProjectClick }) => {
  const navigate = useNavigate();

  const [project, setProject] = useState();
  const [projectId, setProjectId] = useState(localStorage.getItem("selectedProject"));

  const [pageIsLoading, setPageIsLoading] = useState(false);

  const [pieChartData, setPieChartData] = useState([63, 7, 5, 20]);
  const [summary, setSummary] = useState({
    overallScore: 0, passingControls: 0, totalControls: 0, remediations: 0, daysToAudit: 0
  });

  // State to manage which modal is currently active
  const [activeModal, setActiveModal] = useState(null);  

  const dispatch = useDispatch();  
  const { switched } = useSelector((state => state?.rootReducer?.project || {}));

  const { currentUser } = useSelector((state => state?.rootReducer?.users || {}));

  useEffect(() => {
    if(!switched) return;    
    setProjectId(switched?.projectId);
    setProject(switched);
  }, [switched])

  useEffect(() => {
    if (!projectId) return;
    const fetchDashboardDetails = async () => {
      try {        
        setPageIsLoading(true);
        const result = await dispatch(dashboardReport(projectId)).unwrap();
        if(result?.status) {
          setSummary(result?.data?.summary ?? {});
          
          setPieChartData(result?.data?.pieChartData || pieChartData);
          // setPieChartData([2, 3, 0, 15]);
        }
      } catch (e) { console.error(e); } finally { setPageIsLoading(false); }
    };
    fetchDashboardDetails();
    
  }, [projectId]);

  // Render dynamic content based on clicked widget
  const renderModalContent = () => {
    switch (activeModal) {
      case 'score':
        return (
          <div className="space-y-4">
            <div className="p-6 bg-indigo-50 border border-indigo-100 rounded-2xl text-center">
              <h1 className="text-6xl font-black text-indigo-700 mb-2">{summary?.overallScore}%</h1>
              <p className="text-indigo-600 font-bold uppercase tracking-wider">Overall Compliance Readiness</p>
            </div>
            <p className="text-slate-600 mt-4">Your compliance score is calculated based on passing controls across all active frameworks.</p>
          </div>
        );
      case 'controls':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">You have <b>{summary?.passingControls} passing controls</b> out of {summary?.totalControls}.</p>
            {['CC1.1 - Entity demonstrates commitment to integrity', 'CC1.2 - Board of directors exercises oversight', 'CC2.1 - Entity communicates internally'].map((control, i) => (
              <div key={i} className="p-4 border border-slate-100 rounded-xl flex justify-between items-center bg-emerald-50/30 hover:bg-emerald-50 transition-colors">
                <h4 className="font-bold text-slate-900">{control}</h4>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold flex items-center gap-1"><CheckCircle size={12} /> Passing</span>
              </div>
            ))}
          </div>
        );
      case 'remediations':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">Action required: <b>{summary?.remediations} open remediations</b> are currently active.</p>
            {['Missing Encryption Proof', 'Failed Background Check', 'Vendor SLA Expired'].map((issue, i) => (
              <div key={i} className="p-4 border border-slate-100 rounded-xl flex justify-between items-center bg-rose-50/30 hover:bg-rose-50 transition-colors">
                <div>
                  <h4 className="font-bold text-slate-900">{issue}</h4>
                  <p className="text-sm text-slate-500">Severity: High</p>
                </div>
                <button className="px-4 py-2 bg-white border border-slate-200 shadow-sm text-sm font-bold text-slate-700 rounded-lg hover:bg-slate-50">View Details</button>
              </div>
            ))}
          </div>
        );
      case 'audit':
        return (
          <div className="space-y-4">
            <div className="p-6 bg-slate-100 border border-slate-200 rounded-2xl text-center">
              <h1 className="text-6xl font-black text-slate-700 mb-2">{summary?.daysToAudit}</h1>
              <p className="text-slate-500 font-bold uppercase tracking-wider">Days Remaining</p>
            </div>
            <p className="text-slate-600 mt-4">Your next scheduled audit is approaching. Ensure all evidence is uploaded and tasks are completed.</p>
          </div>
        );
      case 'frameworks':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['SOC 2 - 72% Complete', 'ISO 27001 - 48% Complete', 'HIPAA - 64% Complete'].map((fw, i) => (
                <div key={i} className="p-5 border border-indigo-100 rounded-xl bg-indigo-50/30">
                  <h4 className="font-bold text-indigo-900 text-lg mb-2">{fw}</h4>
                  <p className="text-sm text-indigo-700 mb-4">View control breakdown and specific framework requirements.</p>
                  <button className="text-sm font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">Open Framework <ArrowRight size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        );
      case 'status_chart':
        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">Detailed list of controls by their current health status.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100"><p className="text-xl font-bold text-emerald-700">{pieChartData?.[0]} Passing</p></div>
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-100"><p className="text-xl font-bold text-orange-700">{pieChartData?.[1]} Needs Review</p></div>
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-100"><p className="text-xl font-bold text-rose-700">{pieChartData?.[2]} Failing</p></div>
              <div className="p-4 bg-slate-100 rounded-xl border border-slate-200"><p className="text-xl font-bold text-slate-700">{pieChartData?.[3]} Not Started</p></div>
            </div>
          </div>
        );
      case 'risks':
        const dummyRisks = [
          { title: "AI Evaluated Finding", control: "ITGC-AC-01", severity: "High" },
          { title: "Missing Vendor SLA", control: "CC2.3", severity: "High" },
          { title: "Unencrypted S3 Bucket", control: "CC6.1", severity: "Medium" },
        ];

        return (
          <div className="space-y-4">
            <p className="text-slate-600 mb-4">
              Showing top <b>{dummyRisks.length} active risks</b> requiring immediate attention.
            </p>

            <div className="space-y-3 mb-6">
              {dummyRisks.map((risk, i) => (
                <div key={i} className="p-4 border border-slate-200 rounded-xl flex justify-between items-center bg-white hover:bg-slate-50 transition-colors shadow-sm">
                  <div>
                    <h4 className="font-bold text-slate-900">{risk.title}</h4>
                    <p className="text-xs font-medium text-slate-500 mt-0.5">Linked Control: {risk.control}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-md text-xs font-bold
                    ${risk.severity === 'High' ? 'bg-rose-100 text-rose-700' :
                      risk.severity === 'Medium' ? 'bg-orange-100 text-orange-700' :
                        'bg-emerald-100 text-emerald-700'}`}>
                    {risk.severity}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate(RoutesLink.risks)} // Triggers the redirect
              className="w-full py-3.5 bg-indigo-600 text-white font-bold rounded-xl text-sm hover:bg-indigo-700 transition-all shadow-sm active:scale-[0.98] flex items-center justify-center gap-2"
            >
              View All Risks <ArrowRight size={16} />
            </button>
          </div>
        );
      default:
        return (
          <div className="py-8 text-center">
            <Activity className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-lg font-bold text-slate-900">Detailed View</p>
            <p className="text-slate-500 mt-2">More information for this section will be available soon.</p>
          </div>
        );
    }
  };

  const getModalTitle = () => {
    switch (activeModal) {
      case 'score': return 'Compliance Readiness';
      case 'controls': return 'Passing Controls Detail';
      case 'remediations': return 'Open Remediations Detail';
      case 'risks': return 'Top Identified Risks';
      case 'audit': return 'Audit Schedule';
      case 'frameworks': return 'Active Frameworks';
      case 'status_chart': return 'Control Status Breakdown';
      default: return 'Details';
    }
  }

  return (
    
    <Content className="main-body" id="scrolly-div">
      <Spin spinning={pageIsLoading} size="large">

        <div className=" bg-slate-50  relative">

          {/* Interactive Modal Component */}
          <Modal
            isOpen={!!activeModal}
            onClose={() => setActiveModal(null)}
            title={getModalTitle()}
          >
            {renderModalContent()}
          </Modal>

          <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white">
                <LayoutDashboard size={28} strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Project Overview</h3>
                <p className="text-sm font-semibold text-slate-500 mt-0.5">Real-time health of <b>{project?.name || 'your compliance status'}</b>.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* <button className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95" onClick={()=>onNewProjectClick()}>
                  <PlusCircle size={18} />
                  <span>New Project</span>
                </button> */}
            </div>
          </div>
          <div className="max-w-[1600px] mx-auto space-y-8">

            {/* ROW 1: KPI SUMMARY - ALL CLICKABLE */}
            {console.log("summary", summary)}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
              {/* <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"> */}
              {/* <StatCard title="Compliance Score" value={`10%`} trend="+2.4%" icon={<Shield />} colorTheme="indigo" onClick={() => setActiveModal('score')} /> */}
              <StatCard title="Compliance Score" value={`${summary?.overallScore}%`} trend="+2.4%" icon={<Shield />} colorTheme="indigo" onClick={() => setActiveModal('score')} />

              <StatCard title="Controls Passing" value={`${summary?.passingControls}/${summary?.totalControls}`} trend="+12" icon={<CheckCircle />} colorTheme="emerald" onClick={() => setActiveModal('controls')} />
              <StatCard title="Risks"
                value={`${summary?.passingControls}/${summary?.totalControls}`}
                // trend="+12" 
                icon={<AlertTriangle />}
                colorTheme="rose"
                onClick={() => setActiveModal('risks')}
              />

              <StatCard title="Open Remediations" value={summary?.remediations} trendDown icon={<AlertCircle />} colorTheme="rose" onClick={() => setActiveModal('remediations')} />
              <StatCard title="Days to Audit" value={summary?.daysToAudit} icon={<Calendar />} colorTheme="slate" onClick={() => setActiveModal('audit')} />
            </div>

            {/* ROW 2: COMPLIANCE STATUS - CLICKABLE */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FrameworkProgress />
              <ControlsByStatusChart details={pieChartData} />
            </div>

            {/* ROW 3: OPERATIONS STATUS - CLICKABLE */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <WorkflowTasks />
              <EvidenceStatus />
            </div>

            {/* ROW 4: ACTIVITY - CLICKABLE */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <RecentActivity />
              <UpcomingDeadlines />
              <ActionRequired />
            </div>

          </div>
        </div>
    
      </Spin>
    </Content>
  );
};

export default Dashboard;